import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { 
  isAuthenticated as checkAuth,
  logout as logoutUser,
  fetchPermissions
} from 'api/auth'
import {
  getUser,
  setUser as setUserData,
  setAccessToken,
} from 'api/tokenService'
import { MenuPermission } from 'api/auth'
import { AUTH_SESSION_ENDED_EVENT } from 'lib/authSession'
import { appNavigate } from 'lib/navigation'

interface AuthContextValue {
  user: unknown | null
  isAuthenticated: boolean
  permissions: MenuPermission[]
  permissionsLoading: boolean
  authLoading: boolean
  login: (authData: { access_token: string; user?: unknown }) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<unknown | null>(null)
  const [loggedIn, setLoggedIn] = useState(false)
  const [permissions, setPermissions] = useState<MenuPermission[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)

  const clearAuthState = useCallback(() => {
    setUser(null)
    setLoggedIn(false)
    setPermissions([])
    setPermissionsLoading(false)
    setAuthLoading(false)
  }, [])

  useEffect(() => {
    const handleSessionEnded = () => {
      clearAuthState()
      appNavigate('/login', { replace: true })
    }

    window.addEventListener(AUTH_SESSION_ENDED_EVENT, handleSessionEnded)
    return () => window.removeEventListener(AUTH_SESSION_ENDED_EVENT, handleSessionEnded)
  }, [clearAuthState])

  // Initialize state from localStorage on mount and fetch permissions
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const authenticated = checkAuth()
        setLoggedIn(authenticated)

        const userData = getUser()
        setUser(userData)

        // Fetch permissions from backend if authenticated
        if (authenticated) {
          setPermissionsLoading(true)
          try {
            const fetchedPermissions = await fetchPermissions()
            setPermissions(fetchedPermissions)
          } catch (error) {
            console.error('Failed to fetch permissions:', error)
            // Don't clear auth state on permissions error - user is still authenticated
            setPermissions([])
          } finally {
            setPermissionsLoading(false)
          }
        } else {
          setPermissions([])
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        setLoggedIn(false)
        setUser(null)
        setPermissions([])
      } finally {
        setAuthLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (authData: { access_token: string; user?: unknown }) => {
    setAccessToken(authData.access_token)
    if (authData.user) {
      setUserData(authData.user)
      setUser(authData.user)
    }
    setLoggedIn(true)
    
    // Fetch permissions after successful login
    setPermissionsLoading(true)
    try {
      const fetchedPermissions = await fetchPermissions()
      setPermissions(fetchedPermissions)
    } catch (error) {
      console.error('Failed to fetch permissions after login:', error)
      // Don't clear auth state on permissions error - user is still authenticated
      setPermissions([])
    } finally {
      setPermissionsLoading(false)
    }
  }

  const logout = async () => {
    try {
      await logoutUser()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuthState()
      appNavigate('/login', { replace: true })
    }
  }

  const value: AuthContextValue = {
    user,
    isAuthenticated: loggedIn,
    permissions,
    permissionsLoading,
    authLoading,
    login,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
